import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { isGardensClosurePopupDay } from '../../utils/openingHoursUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 520px;
  width: 90%;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideIn 0.3s ease-out forwards;

  @keyframes slideIn {
    from {
      transform: scale(0.8) translateY(-20px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 50%;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const Icon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const Message = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.6;
  margin: 0 0 1.5rem;

  strong {
    color: ${props => props.theme.colors.accent};
    font-weight: 600;
  }
`;

const TimesLink = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.05rem;
  font-weight: 600;
  padding: 0.9rem 1.75rem;
  border-radius: 25px;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
  }
`;

const STORAGE_KEY = 'gardensClosurePopup2Nov2026';

const GardensClosurePopup = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isGardensClosurePopupDay(new Date())) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(event) => event.stopPropagation()} role="dialog" aria-labelledby="gardens-closure-title">
        <CloseButton type="button" onClick={handleClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <Icon>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Icon>
        <Title id="gardens-closure-title">Gardens Closed</Title>
        <Message>
          The Gardens and Adventure Playground are closed today, <strong>Monday 2 November</strong>, and tomorrow, <strong>Tuesday 3 November</strong>.
          <br /><br />
          <strong>The Retail Village is open.</strong> Please enter from Redmile Lane. The Castle remains closed until Christmas.
        </Message>
        <TimesLink to="/your-visit/opening-times" onClick={handleClose}>
          Opening times
        </TimesLink>
      </ModalContent>
    </ModalOverlay>
  );
};

export default GardensClosurePopup;
